import { useEffect, useState } from 'react';
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  Calendar,
  Database,
  FileQuestion,
  FileText,
  LayoutDashboard,
  Mail,
  MessageCircle,
  MessageSquare,
  Settings,
  Share2,
  UserCircle,
  UserPlus,
  Users,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  countUnreadInboxMessages,
  MESSAGING_UNREAD_CHANGED_EVENT,
} from '../../../lib/messaging';
import { menuItemVisibleForRole, type ClinicianRole, type OrgDashboardMenuId } from './therapistJobsAndRoutes';

const DRAWER_HOME: { id: OrgDashboardMenuId; label: string; icon: typeof Users } = {
  id: 'dashboard-home',
  label: 'Dashboard',
  icon: LayoutDashboard,
};

/** Primary actions — open existing full-page flows (not dashboard sections). */
const DRAWER_QUICK_ACTIONS: Array<{ label: string; href: string; icon: typeof Users }> = [
  { label: 'Add client', href: '/client-plan-wizard', icon: UserPlus },
  { label: 'Appointment', href: '/book-meeting', icon: Calendar },
];

/** Screenshot labels from Admin & libraries — therapist dashboard only. */
const DRAWER_ADMIN_ITEMS: Array<{ id: OrgDashboardMenuId; label: string; icon: typeof Users }> = [
  { id: 'overview', label: 'Setup guide', icon: BarChart3 },
  { id: 'messages', label: 'Messages', icon: MessageCircle },
  { id: 'users', label: 'Team', icon: Users },
  { id: 'invitations', label: 'Invitations', icon: Mail },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'questionnaire-management', label: 'Questionnaires', icon: FileText },
  { id: 'question-bank', label: 'Question bank', icon: FileQuestion },
  { id: 'strategy-bank', label: 'Strategy bank', icon: Database },
  { id: 'program-referral', label: 'Program referral', icon: Share2 },
];

/** Opens parent community blog (read-only for therapists). */
const DRAWER_BLOG_LINK = { label: 'Blog', href: '/community', icon: BookOpen };

const DRAWER_PROFILE: { id: OrgDashboardMenuId; label: string; icon: typeof Users } = {
  id: 'my-profile',
  label: 'My profile',
  icon: UserCircle,
};

const DRAWER_ORG_ADMIN_ITEMS: Array<{ id: OrgDashboardMenuId; label: string; icon: typeof Users }> = [
  { id: 'team-activity', label: 'Team activity', icon: Activity },
  { id: 'settings', label: 'Org & plan', icon: Settings },
];

function formatUnreadBadge(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? '9+' : String(count);
}

export function isTherapistPracticeRole(role: ClinicianRole): boolean {
  return ['THERAPIST', 'CLINICIAN', 'INTERNS', 'ORG_ADMIN'].includes(String(role || '').toUpperCase());
}

type TherapistPracticeDrawerProps = {
  role: ClinicianRole;
  selectedMenu: string;
  orgName?: string;
  onSelect: (id: OrgDashboardMenuId) => void;
};

export default function TherapistPracticeDrawer({
  role,
  selectedMenu,
  orgName,
  onSelect,
}: TherapistPracticeDrawerProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onCommunity = location.pathname.startsWith('/community');
  const adminItems = DRAWER_ADMIN_ITEMS.filter((item) => menuItemVisibleForRole(item.id, role));
  const orgItems = DRAWER_ORG_ADMIN_ITEMS.filter((item) => menuItemVisibleForRole(item.id, role));
  const [unreadMessages, setUnreadMessages] = useState(0);
  const isPracticeRole = isTherapistPracticeRole(role);

  useEffect(() => {
    if (!isPracticeRole) {
      setUnreadMessages(0);
      return;
    }
    const userId = user?.id;
    if (!userId) {
      setUnreadMessages(0);
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      const count = await countUnreadInboxMessages(userId);
      if (!cancelled) setUnreadMessages(count);
    };

    void refresh();
    const intervalId = window.setInterval(() => {
      void refresh();
    }, 30000);
    const onUnreadChanged = () => {
      void refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };

    window.addEventListener(MESSAGING_UNREAD_CHANGED_EVENT, onUnreadChanged);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener(MESSAGING_UNREAD_CHANGED_EVENT, onUnreadChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.id, isPracticeRole]);

  if (!isPracticeRole) return null;

  return (
    <aside
      className="sticky top-[60px] z-20 flex h-[calc(100vh-60px)] w-56 shrink-0 flex-col bg-[#0B1220] sm:w-64"
      aria-label="Admin and libraries"
    >
      <div className="flex items-center gap-3 px-4 py-5 sm:px-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
          <Building2 className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold tracking-tight text-white">Admin &amp; libraries</p>
          <p className="mt-0.5 truncate text-[11px] font-medium text-slate-400">
            {orgName?.trim() || 'Setup, team, notes, and libraries'}
          </p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label="Therapist admin menu">
        <DrawerButton
          label={DRAWER_HOME.label}
          icon={DRAWER_HOME.icon}
          active={selectedMenu === DRAWER_HOME.id && !onCommunity}
          onClick={() => onSelect(DRAWER_HOME.id)}
        />
        <ul className="mt-1 space-y-0.5">
          {DRAWER_QUICK_ACTIONS.map((item) => (
            <li key={item.href}>
              <DrawerButton
                label={item.label}
                icon={item.icon}
                active={location.pathname.startsWith(item.href)}
                onClick={() => navigate(item.href)}
              />
            </li>
          ))}
        </ul>
        <p className="mb-2 mt-5 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          Admin &amp; libraries
        </p>
        <ul className="space-y-0.5">
          {adminItems.map((item) => (
            <li key={item.id}>
              <DrawerButton
                label={item.label}
                icon={item.icon}
                active={selectedMenu === item.id}
                badge={item.id === 'messages' ? formatUnreadBadge(unreadMessages) : null}
                onClick={() => onSelect(item.id)}
              />
            </li>
          ))}
          <li>
            <DrawerButton
              label={DRAWER_BLOG_LINK.label}
              icon={DRAWER_BLOG_LINK.icon}
              active={onCommunity}
              onClick={() => navigate(DRAWER_BLOG_LINK.href)}
            />
          </li>
        </ul>
        {orgItems.length > 0 ? (
          <>
            <p className="mb-2 mt-5 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Organization
            </p>
            <ul className="space-y-0.5">
              {orgItems.map((item) => (
                <li key={item.id}>
                  <DrawerButton
                    label={item.label}
                    icon={item.icon}
                    active={selectedMenu === item.id}
                    onClick={() => onSelect(item.id)}
                  />
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {menuItemVisibleForRole(DRAWER_PROFILE.id, role) ? (
          <div className="mt-4 border-t border-white/10 pt-3">
            <DrawerButton
              label={DRAWER_PROFILE.label}
              icon={DRAWER_PROFILE.icon}
              active={selectedMenu === DRAWER_PROFILE.id}
              onClick={() => onSelect(DRAWER_PROFILE.id)}
            />
          </div>
        ) : null}
      </nav>
    </aside>
  );
}

function DrawerButton({
  label,
  icon: Icon,
  active,
  onClick,
  badge,
}: {
  label: string;
  icon: typeof Users;
  active: boolean;
  onClick: () => void;
  badge?: string | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm tracking-tight transition-colors ${
        active
          ? 'bg-indigo-600 font-medium text-white'
          : 'font-medium text-slate-300 hover:bg-white/[0.06] hover:text-white'
      }`}
    >
      <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-white' : 'text-slate-400'}`} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge ? (
        <span
          className={`ml-auto inline-flex min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums ${
            active ? 'bg-white text-indigo-700' : 'bg-rose-500 text-white'
          }`}
          aria-label={`${badge} unread messages`}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}
